import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Col, Input, Label } from 'reactstrap';
import { ImageWithFallback } from 'Components/pos2u/ImageWithFallback';
import { useSettings } from 'pages/SettingsV2/SettingsContext';
import SettingsSelect from 'pages/SettingsV2/SettingsSelect';
import { BARCODE_LAYOUTS } from 'pages/SettingsV2/SettingsConstants';
import {
  BarcodeBrand,
  BarcodeLayoutType,
  BarcodeSize,
  SettingsFormValues,
} from 'pages/SettingsV2/settingsTypes';
import SettingsV2SectionCard from 'pages/SettingsV2/SettingsV2SectionCard';

const BarcodeSectionV2 = () => {
  const { t } = useTranslation();
  const { barcodeOptions } = useSettings();
  const { values, setFieldValue, handleChange } =
    useFormikContext<SettingsFormValues>();
  const { idType, brand, size, layoutType } = values.barcode;

  const brandOptions = barcodeOptions?.brands ?? [];
  const sizeKeys = barcodeOptions?.sizes_by_type?.[String(layoutType)] ?? [];
  const sizeLabels = barcodeOptions?.size_labels?.[brand] ?? {};

  const handleLayoutChange = (next: BarcodeLayoutType) => {
    setFieldValue('barcode.layoutType', next);
    const nextSizes = barcodeOptions?.sizes_by_type?.[String(next)] ?? [];
    if (nextSizes.length > 0 && !nextSizes.includes(size)) {
      setFieldValue('barcode.size', nextSizes[0] as BarcodeSize);
    }
  };

  return (
    <SettingsV2SectionCard
      sectionKey="barcode"
      title={t('Barcode Settings')}
      icon="ri-barcode-line"
    >
      <div className="mb-3 row">
        <Label className="col-sm-3 col-form-label">
          {t('Generate Code From')}
        </Label>
        <Col sm={9}>
          <div className="d-flex flex-column flex-sm-row gap-3 pt-sm-2">
            {(
              [
                { value: 'product_code', label: t('Product Code') },
                { value: 'barcode', label: t('Barcode') },
              ] as { value: 'product_code' | 'barcode'; label: string }[]
            ).map((option) => (
              <div className="form-check" key={option.value}>
                <Input
                  id={`barcode-v2-id-type-${option.value}`}
                  type="radio"
                  name="barcode.idType"
                  value={option.value}
                  checked={idType === option.value}
                  onChange={handleChange}
                />
                <Label check htmlFor={`barcode-v2-id-type-${option.value}`}>
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
        </Col>
      </div>

      <div className="mb-3 row">
        <Label htmlFor="barcode-v2-brand" className="col-sm-3 col-form-label">
          {t('Label Brand')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="barcode-v2-brand"
            value={brand}
            onChange={(value) =>
              setFieldValue('barcode.brand', value as BarcodeBrand)
            }
            options={brandOptions.map((option) => ({
              value: option.value as BarcodeBrand,
              label: option.label,
            }))}
          />
        </Col>
      </div>

      <div className="mb-3 row">
        <Label htmlFor="barcode-v2-size" className="col-sm-3 col-form-label">
          {t('Label Size')}
        </Label>
        <Col sm={9}>
          <SettingsSelect
            inputId="barcode-v2-size"
            value={size}
            onChange={(value) =>
              setFieldValue('barcode.size', value as BarcodeSize)
            }
            options={sizeKeys.map((key) => ({
              value: key as BarcodeSize,
              label: sizeLabels[key] ?? key.toUpperCase(),
            }))}
          />
        </Col>
      </div>

      <div className="row">
        <Label className="col-sm-3 col-form-label">{t('Label Layout')}</Label>
        <Col sm={9}>
          <div className="d-flex gap-3 overflow-auto pb-2">
            {BARCODE_LAYOUTS.map((layout) => {
              const selected = layoutType === layout.value;
              return (
                <button
                  type="button"
                  key={layout.value}
                  onClick={() => handleLayoutChange(layout.value)}
                  className={`btn p-2 border rounded flex-shrink-0 ${
                    selected ? 'border-primary border-2' : 'border-light'
                  }`}
                  style={{ width: 128 }}
                >
                  <ImageWithFallback
                    src={layout.image}
                    alt={`${t('Label Layout')} ${layout.value}`}
                    style={{ width: '100%', height: 78, objectFit: 'contain' }}
                  />
                  <div className="text-center fs-13 mt-2">
                    {t('Type')} {layout.value}
                  </div>
                </button>
              );
            })}
          </div>
        </Col>
      </div>
    </SettingsV2SectionCard>
  );
};

export default BarcodeSectionV2;
